/**
 * @fileoverview Lazily-initialized Firebase app instance for the application.
 * @description Creates the single FirebaseApp every Firebase product (Analytics, Cloud Messaging)
 * is built on top of, only when a Firebase project has actually been configured. Returns null
 * otherwise, so the app behaves identically (no Firebase, no error) in environments — like local
 * development today — where no Firebase project has been provisioned yet, the same inert-until-
 * configured posture src/config/sentry.ts takes with its DSN.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app'

import { firebaseConfig, isFirebaseConfigured } from '@/config/firebase-config'

let cachedApp: FirebaseApp | null = null

/**
 * @description Returns the app's single FirebaseApp instance, creating it on first call. Reuses
 * an already-initialized app (e.g. from Vite's HMR re-running this module) instead of calling
 * initializeApp twice, which Firebase would otherwise throw on.
 * @returns {FirebaseApp | null} The FirebaseApp, or null when no Firebase project is configured.
 */
export function getFirebaseApp(): FirebaseApp | null {
  if (!isFirebaseConfigured) {
    return null
  }

  if (!cachedApp) {
    cachedApp = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig)
  }

  return cachedApp
}
